import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useProjectManagers } from '../hooks/useProjectManagers';
import { useAssignLead } from '../hooks/useAssignLead';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';

interface AssignLeadModalProps {
  leadId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for assigning a lead to an estimator
 * Allows selecting an estimator and optionally overriding their commission rate
 */
export function AssignLeadModal({
  leadId,
  isOpen,
  onClose,
  onSuccess,
}: AssignLeadModalProps) {
  const [selectedProjectManagerId, setSelectedProjectManagerId] = useState<string>('');
  const [commissionOverride, setCommissionOverride] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  const { projectManagers, isLoading: isLoadingProjectManagers, error: projectManagersError } = useProjectManagers();
  const { assignLead, isLoading: isSubmitting, error: submitError } = useAssignLead();
  const { hasPermission } = useAuth();
  const canViewCommission = hasPermission(Permissions.CommissionsRead);

  // Find the selected project manager to display their default commission rate
  const selectedProjectManager = projectManagers.find(
    (pm) => pm.id.toString() === selectedProjectManagerId
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setSelectedProjectManagerId('');
        setCommissionOverride('');
        setValidationError('');
      }, 0);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    // Validate project manager selection
    if (!selectedProjectManagerId) {
      setValidationError('Please select a project manager');
      return;
    }

    // Validate commission override if provided
    let commissionValue: number | undefined = undefined;
    if (commissionOverride.trim()) {
      const parsed = parseFloat(commissionOverride);
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        setValidationError('Commission override must be between 0 and 100');
        return;
      }
      commissionValue = parsed;
    }

    try {
      await assignLead(leadId, parseInt(selectedProjectManagerId, 10), commissionValue);
      onSuccess();
      onClose();
    } catch {
      // Error is handled by the hook and displayed below
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Lead</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorAlert error={validationError || submitError || projectManagersError} />

          {/* Project Manager Selection */}
          <div className="space-y-2">
            <Label htmlFor="projectManager">Project Manager *</Label>
            <Select
              id="projectManager"
              value={selectedProjectManagerId}
              onChange={(e) => setSelectedProjectManagerId(e.target.value)}
              disabled={isLoadingProjectManagers || isSubmitting}
            >
              <option value="">Select a project manager</option>
              {projectManagers.map((projectManager) => (
                <option key={projectManager.id} value={projectManager.id}>
                  {projectManager.firstName} {projectManager.lastName}
                  {canViewCommission && ` (${projectManager.commissionRate}%)`}
                </option>
              ))}
            </Select>
          </div>

          {/* Default Commission Rate Display - Only visible to users with commission read permission */}
          {canViewCommission && selectedProjectManager && (
            <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 border border-blue-200">
              Default commission rate: {selectedProjectManager.commissionRate}%
            </div>
          )}

          {/* Commission Override - Only visible to users with commission read permission */}
          {canViewCommission && (
            <div className="space-y-2">
              <Label htmlFor="commissionOverride">Commission Rate Override (%)</Label>
              <Input
                id="commissionOverride"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Leave blank to use default rate"
                value={commissionOverride}
                onChange={(e) => setCommissionOverride(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-sm text-gray-600">
                Leave blank to use the project manager&apos;s default rate
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="action" disabled={isSubmitting || isLoadingProjectManagers}>
              {isSubmitting ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
